import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { InventoryTypeEnum, formatPriceRange, mapInventoryTypeToLabel, nor, or } from 'dash-common';
import {
  TableWitPagination,
  SortHeader,
  Text,
  Badge,
  Box,
  DropDown,
  Icon,
  formatBathroomsRangeValues,
  formatBedroomsRangeValues,
  Select,
  Flex,
  formatGeneralRangeValues,
} from 'dash-ui';
import {
  UnitFragment,
  GetUnitsQuery,
} from 'gql/generated.graphql';
import { isNil } from 'lodash';
import { BADGE_TO_INVENTORY_TYPE_COLOR_MAP } from '../../PropetyInventory.constants';

const TableBadge = styled(Badge)`
  max-width: 112px;
`;

const StyleSelect = styled(Select)`
  .select__placeholder {
    font-size: 12px;
    color: black;
  }
`;

const StyleBox = styled(Box)`
  background-color: white;
  border: 1px solid ${({ theme }) => theme.colors.n40};
  border-radius: ${({ theme }) => theme.space * 2}px;
`;

function PropertyInventoryTableUI(
  {
    data,
    loading,
    placeholder,
    handleEditClick,
    handleAddClick,
    handleDeleteClick,
    handleChangeType,
    handleCopyClick,
  }:
  {
    data: GetUnitsQuery['getUnits']['collection'],
    loading: boolean,
    handleEditClick: (original: UnitFragment) => void,
    handleAddClick: (original: UnitFragment) => void,
    handleDeleteClick: (original: UnitFragment) => void,
    handleCopyClick: (original: UnitFragment) => void,
    handleChangeType: (inventoryType: InventoryTypeEnum, unit: UnitFragment) => void,
    placeholder: string,
  }) {
  const theme = useTheme();
  const { t: tTranslation } = useTranslation();
  const { t } = useTranslation('tables', {
    keyPrefix: 'inventory',
  });
  const { t: tLabels } = useTranslation('common', { keyPrefix: 'labels' });

  // FIXME: re-creating context functions everytime state change. Use useCallback
  const columns = React.useMemo(
    () => [
      {
        Header: () => (
          <Box mr={5}>
            <SortHeader accessor="name" isDefault>
              <Text whiteSpace="nowrap">{t`name`}</Text>
            </SortHeader>
          </Box>
        ),
        accessor: 'name',
        name: 'name',
        Cell: ({ value }: { value: string }) => {
          return (
            <Box mr={5}>
              <Text ellipsis>{value}</Text>
            </Box>
          );
        }
      },
      {
        Header: () => (
          <Box mr={2}>
            <SortHeader accessor="averageBedroomsNumber">
              <Text whiteSpace="nowrap">{t`bedrooms`}</Text>
            </SortHeader>
          </Box>
        ),
        accessor: 'averageBedroomsNumber',
        name: 'averageBedroomsNumber',
        Cell: ({ value }: { value: { min: number; max: number } }) => (
          <Text>
            {formatBedroomsRangeValues(value, {
              isNullable: (value?.min || 0) === 0 && !value?.max,
              t: tTranslation,
            })}
          </Text>
        ),
      },
      {
        Header: () => (
          <Box mr={2}>
            <SortHeader accessor="averageBathroomsNumber">
              <Text whiteSpace="nowrap">
                {t`bathrooms`}
              </Text>
            </SortHeader>
          </Box>
        ),
        accessor: 'averageBathroomsNumber',
        name: 'averageBathroomsNumber',
        Cell: ({ value }: { value: { min: number; max: number } }) => (
          <Text>{formatBathroomsRangeValues(value)}</Text>
        ),
      },
      {
        Header: () => (
          <Box mr={2}>
            <SortHeader accessor="customRooms">
              <Text whiteSpace="nowrap" color={theme.colors.n80}>{t('customRooms')}</Text>
            </SortHeader>
          </Box>
        ),
        accessor: 'customRooms',
        name: 'customRooms',
        Cell: ({ value }: { value: number }) => (
          <Box maxWidth={190}>
            <Text ellipsis>{value}</Text>
          </Box>
        ),
      },
      {
        Header: () => (
          <Box mr={2}>
            <SortHeader accessor="breakdownSqft">
              <Text whiteSpace="nowrap" color={theme.colors.n80}>{t('breakdownSqft')}</Text>
            </SortHeader>
          </Box>
        ),
        accessor: 'breakdownSqft',
        name: 'breakdownSqft',
        Cell: ({
          row: { original },
        }: {
          row: { original: UnitFragment };
        }) => (
          <StyleSelect
            w="100%"
            maxWidth={100}
            placeholder="SQFT"
            isSearchable={false}
            isOptionDisabled={(option: any) => option.disabled}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 150 }),
            }}
            components={{
              Menu: () => (
                <StyleBox minWidth="fit-content" w="100%" px={2} py={1}>
                  <Flex minWidth="fit-content" flexDirection="column" w="100%">
                    <Flex minWidth="fit-content" justifyContent="space-between" w="100%" my={1} mb={1}>
                      <Text whiteSpace="nowrap">{tTranslation('pages:portalShell.intSqft')}</Text>
                      <Box mr={5} />
                      <Text whiteSpace="nowrap">
                        {formatGeneralRangeValues(original.averageInteriorSqft)}
                      </Text>
                    </Flex>
                    <Flex justifyContent="space-between" w="100%" mb={1}>
                      <Text whiteSpace="nowrap">{tTranslation('pages:portalShell.extSqft')}</Text>
                      <Box mr={5} />
                      <Text whiteSpace="nowrap">
                        {formatGeneralRangeValues(original.averageExteriorSqft)}
                      </Text>
                    </Flex>
                    <Flex justifyContent="space-between" w="100%" my={1}>
                      <Text whiteSpace="nowrap">{tTranslation('pages:portalShell.totalSqft')}</Text>
                      <Box mr={5} />
                      <Text whiteSpace="nowrap">
                        {formatGeneralRangeValues(original.averageTotalSqft)}
                      </Text>
                    </Flex>
                  </Flex>
                </StyleBox>
              ),
            }}
          />
        ),
      },
      {
        Header: () => (
          <Box mr={2}>
            <SortHeader accessor="startingPrice">
              <Text whiteSpace="nowrap">
                {t`priceRange`}
              </Text>
            </SortHeader>
          </Box>
        ),
        accessor: 'startingPrice',
        name: 'startingPrice',
        Cell: ({
          value,
          row,
        }: {
          value: number;
          row: { original: { highestPrice: number; startingPrice: number } };
        }) => {
          const [min, max] = [value, row.original.highestPrice];

          return (
            <Text>
              {formatPriceRange(min, max, {
                shortMax: true,
                shortMin: true,
                prefix: tLabels('from') || '',
              })}
            </Text>
          );
        },
      },
      {
        Header: () => (
          <Box mr={2}>
            <SortHeader accessor="inventoryType">
              <Text whiteSpace="nowrap">
                {t('inventoryType')}
              </Text>
            </SortHeader>
          </Box>
        ),
        accessor: 'inventoryType',
        name: 'inventoryType',
        Cell: ({
          row: { original },
        }: {
          row: { original: UnitFragment };
        }) => (
          <DropDown
            placement="bottom"
            placementHorizontal="left"
            Anchor={({ isOpen }) => (
              <Badge
                h={32}
                w={100}
                square
                type={BADGE_TO_INVENTORY_TYPE_COLOR_MAP[original.inventoryType as InventoryTypeEnum]}
                iconRight={isOpen ? 'arrowUp' : 'arrowDown'}
                title={mapInventoryTypeToLabel[original.inventoryType as InventoryTypeEnum]}
              />
            )}
            items={[
              {
                key: InventoryTypeEnum.MODEL,
                children: mapInventoryTypeToLabel[InventoryTypeEnum.MODEL],
                onClick: () => handleChangeType(InventoryTypeEnum.MODEL, original),
              },
              {
                key: InventoryTypeEnum.FOR_SALE,
                children: mapInventoryTypeToLabel[InventoryTypeEnum.FOR_SALE],
                onClick: () => handleChangeType(InventoryTypeEnum.FOR_SALE, original),
              },
            ]}
          />
        ),
      },
      {
        Header: () => (
          <Box mr={2}>
            <Text whiteSpace="nowrap" color={theme.colors.n80}>{t`floorplan`}</Text>
          </Box>
        ),
        accessor: 'floorPlanFile',
        Cell: ({
          value,
          row: { original },
        }: {
          value: { publicUrl: string };
          row: { original: UnitFragment };
        }) =>
          value ? (
            <TableBadge
              square
              iconRight="show"
              type="neutral"
              title={tLabels`view`}
              iconRightProps={{ size: 16 }}
              onClick={() => window?.open(value.publicUrl, '_blank')?.focus()}
            />
          ) : (
            <TableBadge
              square
              iconRight="plus"
              iconRightProps={{ size: 9 }}
              type="active"
              title={tLabels`add`}
              onClick={() => handleAddClick(original)}
            />
          ),
        name: 'floorPlanFile',
      },
      {
        Header: () => (
          <Text whiteSpace="nowrap" color={theme.colors.n80}>{tLabels`actions`.toUpperCase()}</Text>
        ),
        accessor: '_id',
        Cell: ({ row: { original } }: { row: { original: UnitFragment } }) => (
          <Box w={16}>
            <DropDown
              w={70}
              placementHorizontal="left"
              Anchor={() => (
                <Flex w={120} justifyContent="center">
                  <Icon name="dots" />
                </Flex>
              )}
              items={[
                {
                  key: 'edit',
                  icon: 'edit',
                  children: tLabels`edit`,
                  onClick: () => handleEditClick(original),
                },
                {
                  key: 'content_copy',
                  icon: 'contentCopy',
                  children: tLabels`contentCopy`,
                  onClick: () => handleCopyClick(original)
                },
                {
                  key: 'delete',
                  icon: 'close',
                  children: tLabels`delete`,
                  onClick: () => handleDeleteClick(original)
                },
              ]}
            />
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, tLabels]
  );

  return (
    <TableWitPagination
      w="100%"
      tableProps={{
        data,
        loading,
        columns,
        placeholder,
      }}
      paginationProps={{
        label: t`paginationLabel`,
        previousButtonLabel: tLabels`previous`,
        nextButtonLabel: tLabels`next`,
      }}
    />
  );
}

export default PropertyInventoryTableUI;
